<template>
  <div v-if="path" class="retention-editor-page retention-editor-body">
    <div class="retention-editor-detail">
      <div class="retention-editor-name">
        <div>
          <RfBreadcrumbs
            breadcrumbKey="retention-creative"
            :pathName="path.name"
            :sequence="{ id: path.sequence_id, name: path.sequence_name }"
          />
          <h3 class="text-heading-1">{{ path.name }}</h3>
        </div>
        <div class="flex items-center gap-4">
          <RfMenu v-if="!userStore.isReadOnly" buttonType="tertiary" buttonSize="base" sameWidth>
            <template #activator> Copy/Paste Properties </template>
            <template #menu="{ close }">
              <button
                class="flex w-full items-center gap-3 px-4 py-2 pt-4!"
                @click="handleCopyActions(close)"
              >
                <span>Copy Properties</span>
              </button>
              <button class="flex w-full px-4 py-2 pb-4" @click="openPasteConfirm(close)">
                <span>Paste Properties</span>
              </button>
            </template>
          </RfMenu>

          <RfButtonNew
            v-if="
              (currApp['has_styles?'] && currApp.flags.ai_tool) || userStore.isCurrentSuperAdmin
            "
            type="quaternary"
            class="border-beige-90 text-beige-90 fill-beige-90"
            @click="applyGlobalStyles"
          >
            Apply Styles
          </RfButtonNew>
          <RfButtonNew
            v-if="currApp.flags.device_api_implementation"
            type="quaternary"
            class="border-beige-90 text-beige-90 fill-beige-90"
            @click="$refs.devicePayload.show(currLanguage)"
          >
            View payload
            <RfDeviceApiPayloadModal ref="devicePayload" />
          </RfButtonNew>
          <RfTestEmailSender
            v-if="isEmail"
            :path="path"
            :disabled="userStore.isDisabledWithMessage"
            class="mr-2"
          />
          <RfTestPushSender
            v-if="isPush"
            :path="path"
            :disabled="userStore.isDisabledWithMessage"
            class="mr-2"
          />
          <RfButtonNew
            v-if="isStandaloneWeb"
            type="quaternary"
            class="border-beige-90 text-beige-90 fill-beige-90"
            @click="() => domainChooseDialog?.open()"
          >
            <RfOpenInNewIcon class="h-6 w-6" /> Live Preview
          </RfButtonNew>
          <RfButtonNew
            :disabled="userStore.isDisabledWithMessage"
            @click="submitConfigurationAndStay"
          >
            <RfSaveIcon class="h-6 w-6" /> Save
          </RfButtonNew>
          <RfButtonNew
            type="secondary"
            :disabled="userStore.isDisabledWithMessage"
            @click="submitConfigurationAndExit"
          >
            <RfExitIcon class="h-6 w-6" /> Save & Exit
          </RfButtonNew>
        </div>
      </div>
    </div>
    <RfRetentionCreativeEditor ref="editor" :model="path" />
    <RfDomainChooserWrapper ref="domainChooseDialog" @open="openLivePreview" />
    <RfDialog
      ref="pasteConfirmDialog"
      title="Paste Properties"
      message="Pasting properties will override any unsaved changes in the Properties panel. When copying from a different prompt type, expect that some properties may not translate and could cause display issues."
      confirmText="Paste"
      cancelText="Cancel"
      width="560"
      @confirm="handlePasteActions"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, useTemplateRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { cloneDeep } from "lodash-es";
import RfRetentionCreativeEditor from "@/components/RfPathCreate/RfRetentionCreativeEditor.vue";
import RfDomainChooserWrapper from "@/components/RfCommonCards/RfDomainChooserWrapper.vue";
import { useLive } from "@/utils/composables/useLive";
import RfTestEmailSender from "@/components/RfPromotions/RfTestEmailSender.vue";
import RfTestPushSender from "@/components/RfPromotions/RfTestPushSender.vue";
import html2canvas from "html2canvas";
import RfDeviceApiPayloadModal from "@/components/RfDeviceApiPayloadModal.vue";
import { useToastsStore } from "@/pinia/toastsStore";
import {
  isEmail as isEmailFn,
  isStandaloneWeb as isStandaloneWebFn,
  isPushNotification,
} from "@/utils/prompts/promptHelpers";
import { DefaultDeviceOptions } from "@/utils/constants/DevicesConstants";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import { useExperimentsStore } from "@/pinia/experimentsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { usePromptStore } from "@/pinia/promptStore";
import { useUserStore } from "@/pinia/userStore";
import RfBreadcrumbs from "../components/RfBreadcrumbs.vue";
import RfExitIcon from "@/components/icons/RfExitIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfSaveIcon from "@/components/icons/RfSaveIcon.vue";
import RfOpenInNewIcon from "@/components/icons/RfOpenInNewIcon.vue";
import { useDynamicTitle } from "@/utils/composables/useDynamicTitle";
import { useRecentActivity } from "@/utils/composables/useRecentActivity";
import { copyPromptActions, pastePromptActions } from "@/utils/promptActionsClipboard";
import RfMenu from "@/components/menus/RfMenu.vue";
import RfDialog from "@/components/modals/RfDialog.vue";

const route = useRoute();
const router = useRouter();
const toastsStore = useToastsStore();
const experimentsStore = useExperimentsStore();
const promptStore = usePromptStore();
const appsStore = useAppsStore();
const userStore = useUserStore();
const legacyLastErrorStore = useLegacyLastErrorStore();
const recentActivity = useRecentActivity();
const live = useLive();
const domainChooseDialog = useTemplateRef("domainChooseDialog");
const pasteConfirmDialog = useTemplateRef("pasteConfirmDialog");

const path = ref(null);
const editor = ref(null);
const devicePayload = ref(null);

const lastError = computed(() => legacyLastErrorStore.lastError);
const currPath = computed(() => promptStore.prompt);
const currLanguage = computed(() => promptStore.currLanguage);
const currApp = computed(() => appsStore.app);
const isStandaloneWeb = computed(() => isStandaloneWebFn(path.value));
const isEmail = computed(() => isEmailFn(path.value));
const isPush = computed(() => isPushNotification(path.value));

watch(lastError, to => {
  if (to) {
    toastsStore.create({ type: "error", body: to.message });
  }
});

onMounted(async () => {
  useDynamicTitle(() => path.value?.name && `Design | ${path.value.name}`);

  const appId = route.params.aid;
  const pathId = route.params.pid;
  await Promise.all([
    promptStore.getPrompt({ appId, pathId }),
    experimentsStore.getExperiments(pathId),
  ]);
  recentActivity.updatePathActivity(appId, pathId);
  path.value = currPath.value;
});

async function submitConfigurationAndExit() {
  await submitConfiguration(true);
}

async function applyGlobalStyles() {
  const pathClone = cloneDeep(path.value);
  const { styles } = currApp.value;

  const mappings = {
    rf_settings_bg_image_color: "body.background-color",
    rf_settings_fill_color: "body.color",
    button1_bg_color: "button.background-color",
    button1_text_color: "button.color",
    button2_text_color: "button2.color",
    button3_text_color: "button2.color",
    rf_settings_title_font_size: "title.font-size",
    rf_settings_message_font_size: "message.font-size",
    rf_settings_mobile_title_font_size: "title2",
  };

  for (const [key, stylePath] of Object.entries(mappings)) {
    const styleKeys = stylePath.split(".");
    const value = styleKeys.reduce((acc, k) => acc && acc[k], styles);
    if (pathClone.actions[key] && value) {
      pathClone.actions[key] = value;
    }
  }
  const metadata = fontMetadata(styles["@font-face"]["src-published"] || styles["@font-face"].src);
  const internalFont = metadata.find(font => font.type === "internal");
  const externalFont = metadata.find(font => font.type === "external");

  if (internalFont) {
    pathClone.actions.rf_settings_custom_css += `
@font-face {
  font-family: "${styles["@font-face"]["font-family"]}";
  font-style: "${styles["@font-face"]["font-style"]}";
  font-weight: "${styles["@font-face"]["font-weight"]}";
  src: "${styles["@font-face"]["src-published"].replace(/"/g, "'")}";
}
        `;
  } else if (externalFont) {
    pathClone.actions.rf_settings_custom_css += `\n@import url('${externalFont.url}');\n`;
  }
  const overlay = ["retention_modal", "video", "widget", "interstitial", "bottom_banner"];
  const banner = ["horizontal", "tile", "vertical"];
  if (overlay.includes(pathClone.path_type) && styles["@font-face"]["font-family"]) {
    if (
      pathClone.path_type === "interstitial" &&
      ["ios", "android_os"].includes(pathClone.device_type)
    ) {
      pathClone.actions.rf_settings_custom_css += `.mweb-widget-container {
  font-family: ${styles["@font-face"]["font-family"]}
}\n`;
    }
    if (pathClone.device_type !== "ios") {
      pathClone.actions.rf_settings_custom_css += `
.rfmodal-header h1, .rfmodal-message, .rfmodal-footer button, .rfmodal-countdown,
.rfmodal-header-mobileweb h3, .rfmodal-body-mobileweb .rfmodal-message-mobileweb,
.rfmodal-footer-mobileweb, .rfmodal-footer-mobileweb button, .modal-privacy-tos {
  font-family: ${styles["@font-face"]["font-family"]};
}\n`;
    }
  } else if (banner.includes(pathClone.path_type)) {
    pathClone.actions.rf_settings_custom_css += `.promo-tile-wrapper-container,
.rtile-mweb-content{
  font-family: ${styles["@font-face"]["font-family"]}\n;
}`;
  } else if (pathClone.path_type == "text") {
    pathClone.actions.rf_settings_custom_css += `.promo-text-wrapper, .promo-text-wrapper-mobile {
  font-family: ${styles["@font-face"]["font-family"]}\n;
};`;
  } else if (pathClone.path_type === "email") {
    pathClone.actions.rf_settings_custom_css += `.pr-email-wrapper, .pr-email-footer {
  font-family: ${styles["@font-face"]["font-family"]}\n;
}`;
  }
  path.value = pathClone;
}

function fontMetadata(cssText) {
  const fontURLs = [];
  const fontExtensions = [".woff", ".woff2", ".ttf", ".otf", ".eot", ".svg"];

  const urlRegex = /url\(["']?(.*?)["']?\)/g;
  let match;

  while ((match = urlRegex.exec(cssText)) !== null) {
    fontURLs.push(match[1]);
  }

  function checkFontURL(url) {
    if (url.includes("fonts.googleapis.com") || url.includes("fonts.gstatic.com")) {
      return "external";
    }

    for (const extension of fontExtensions) {
      if (url.endsWith(extension)) {
        return "internal";
      }
    }
    return undefined;
  }

  const results = fontURLs.map(url => ({
    url,
    type: checkFontURL(url),
  }));

  return results;
}

async function submitConfigurationAndStay() {
  await submitConfiguration(false);
}

async function emailScreenshot(pathValue) {
  const desktopButton = document.querySelector(".desktop-email-tab");
  if (desktopButton) {
    await desktopButton.click();
  }
  const preview = document.querySelector(".pr-email-preview");
  await html2canvas(preview, { allowTaint: true, useCORS: true }).then(canvas => {
    const image = canvas.toDataURL("image/png");
    pathValue.actions.rf_settings_email_preview_composite = image;
  });
}

async function submitConfiguration(exitOnSave = false) {
  try {
    const newPath = cloneDeep(path.value);

    // Temporarily reset zoom to 100% for accurate composite image capture
    let originalZoom;
    let deviceComponent;
    if (editor.value) {
      // Find the device component with zoomValue (from ZoomPreviewMixin)
      deviceComponent =
        editor.value.$refs?.deviceComponent ||
        Object.values(editor.value.$refs || {}).find(
          ref => ref && typeof ref.zoomValue !== "undefined",
        );

      if (deviceComponent && typeof deviceComponent.zoomValue !== "undefined") {
        // Disable transition for instant zoom change
        deviceComponent.disableZoomTransition = true;

        originalZoom = deviceComponent.zoomValue;
        deviceComponent.zoomValue = 1;

        // Wait for Vue to update the DOM (no transition delay needed)
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      await editor.value.fillModel(newPath);

      // Restore transition and zoom for Save button (not needed for Save & Exit)
      if (!exitOnSave && deviceComponent && originalZoom !== undefined) {
        deviceComponent.disableZoomTransition = false;
        deviceComponent.zoomValue = originalZoom;
      }
    }
    if (isEmail.value) await emailScreenshot(newPath);

    const isNewAndroidEditor =
      currApp.value?.flags?.new_android_editor &&
      [DefaultDeviceOptions.android_os.value, DefaultDeviceOptions.android_tv.value].includes(
        newPath.device_type,
      );
    const isNewAppleEditor =
      !currApp.value?.flags?.legacy_apple_editor &&
      [DefaultDeviceOptions.ios.value, DefaultDeviceOptions.tv_os.value].includes(
        newPath.device_type,
      );

    if (isNewAndroidEditor || isNewAppleEditor) {
      if (!newPath.actions.rf_retention_button2_text)
        newPath.actions.rf_settings_confirm_button_2_enabled = "false";
      if (!newPath.actions.rf_retention_button3_text)
        newPath.actions.rf_settings_cancel_button_enabled = "false";
    }
    await promptStore.updatePrompt({
      appId: route.params.aid,
      pathId: route.params.pid,
      modelPath: newPath,
    });

    if (!currPath.value || exitOnSave) {
      return router.push({
        path: `/apps/${route.params.aid}/retentions/${route.params.pid}`,
      });
    }
    toastsStore.create({ type: "success", body: "Prompt updated successfully" });
    path.value = cloneDeep(currPath.value);
  } catch (error) {
    let timeout;
    let errorToShow = error;
    if (error === "toCanvas_error") {
      errorToShow = new Error(
        "Failed to update prompt. Please check your configuration, including for invalid HTML",
      );
      timeout = 5000;
    }

    toastsStore.create({ type: "error", body: errorToShow.message, timeout });
  }
  return null;
}

async function handleCopyActions(close) {
  try {
    await copyPromptActions(path.value.actions, {
      sourceDeviceType: path.value.device_type,
      sourcePathType: path.value.path_type,
    });
    close();
  } catch (error) {
    toastsStore.create({ type: "error", body: error.message });
  }
}

const openPasteConfirm = close => {
  close();
  pasteConfirmDialog.value?.open();
};

async function handlePasteActions() {
  try {
    const updatedActions = await pastePromptActions(path.value.actions, {
      targetDeviceType: path.value.device_type,
      targetPathType: path.value.path_type,
    });
    path.value = { ...path.value, actions: updatedActions };
  } catch (error) {
    toastsStore.create({ type: "error", body: error.message });
  }
}

function openLivePreview(domain) {
  live.viewPreview(domain, { actionGroupId: path.value.action_group_id });
  domainChooseDialog.value?.close();
}

defineExpose({ editor, devicePayload });
</script>
